/**
 * O contrato de Termos, do lado da tela — espelho das respostas de
 * `bff/v1/termo-adesao` (frente F11, migração 0251). Contrato completo em
 * `docs/credenciamento-ponta-a-ponta/frentes/F11.md`, seção 2.3.
 *
 * Toda regra (quem decide, CADIN, as duas assinaturas, garantia por modelo) é
 * conferida no banco. A tela mostra o que o servidor diz — inclusive as ações
 * que o usuário pode executar (`acoes`), para não renderizar o que ele não pode
 * fazer (CLAUDE.md, regras de interface, item 7).
 */

import type { StatusVariant } from '@/ui/StatusBadge';

// ---------------------------------------------------------------- status

/** Espelha o enum `status_termo` do banco. */
export const STATUS_TERMO = [
  'RASCUNHO',
  'ASSINATURA_PENDENTE',
  'ATIVO',
  'EM_RESCISAO',
  'RESCINDIDO',
  'EXPIRADO',
] as const;
export type StatusTermo = (typeof STATUS_TERMO)[number];

export const ROTULO_STATUS_TERMO: Record<StatusTermo, string> = {
  RASCUNHO: 'Rascunho',
  ASSINATURA_PENDENTE: 'Assinatura pendente',
  ATIVO: 'Ativo',
  EM_RESCISAO: 'Em rescisão',
  RESCINDIDO: 'Rescindido',
  EXPIRADO: 'Expirado',
};

export const VARIANTE_STATUS_TERMO: Record<StatusTermo, StatusVariant> = {
  RASCUNHO: 'rascunho',
  ASSINATURA_PENDENTE: 'assinatura_pendente',
  ATIVO: 'ativo',
  EM_RESCISAO: 'em_rescisao',
  RESCINDIDO: 'rescindido',
  EXPIRADO: 'expirado',
};

// ------------------------------------------------------------ assinatura

export type PapelSignatario = 'credenciado' | 'gestor_publico';
export type StatusConvite =
  'PENDENTE' | 'NO_PROVEDOR' | 'ASSINADO' | 'RECUSADO' | 'EXPIRADO' | 'ENCERRADO_TROCA_VERSAO';

export const ROTULO_PAPEL: Record<PapelSignatario, string> = {
  credenciado: 'Credenciado',
  gestor_publico: 'DTP (gestor público)',
};

export const ROTULO_CONVITE: Record<StatusConvite, string> = {
  PENDENTE: 'Aguardando assinatura',
  NO_PROVEDOR: 'No provedor de assinatura',
  ASSINADO: 'Assinado',
  RECUSADO: 'Recusado',
  EXPIRADO: 'Prazo encerrado',
  ENCERRADO_TROCA_VERSAO: 'Substituído por nova versão',
};

export const VARIANTE_CONVITE: Record<StatusConvite, StatusVariant> = {
  PENDENTE: 'pendente',
  NO_PROVEDOR: 'pendente',
  ASSINADO: 'aprovado',
  RECUSADO: 'rejeitado',
  EXPIRADO: 'expirado',
  ENCERRADO_TROCA_VERSAO: 'inativo',
};

export interface Assinatura {
  conviteId: string;
  papel: PapelSignatario;
  status: StatusConvite;
  signatarioNome: string;
  expiraEm: string;
  assinadoEm: string | null;
  motivo: string | null;
}

// ------------------------------------------------------------------ CADIN

export type SituacaoCadin = 'NADA_CONSTA' | 'INSCRITO';

export const ROTULO_SITUACAO_CADIN: Record<SituacaoCadin, string> = {
  NADA_CONSTA: 'Nada consta (não inscrito)',
  INSCRITO: 'Inscrito no CADIN',
};

export type ImpedimentoCadin = 'cadin_positivo' | 'cadin_sem_declaracao';

export const ROTULO_IMPEDIMENTO: Record<ImpedimentoCadin, string> = {
  cadin_positivo: 'O credenciado consta no CADIN e não pode assinar o termo (edital, item 3.5.2).',
  cadin_sem_declaracao: 'Falta registrar a declaração de inexistência no CADIN (Anexo III).',
};

export interface DeclaracaoCadin {
  situacao: SituacaoCadin;
  declaradaEm: string;
  referencia: string;
  registradaEm: string;
}

// ---------------------------------------------------------------- termos

/** Uma linha da listagem (`GET /termos`). */
export interface TermoResumo {
  id: string;
  numeroTermo: string | null;
  numeroSei: string | null;
  status: StatusTermo;
  dataInicio: string;
  dataFim: string;
  mesRenovacaoRef: number | null;
  credenciadoId: string;
  credenciadoNome: string | null;
  documentoMascarado: string | null;
  assinaturas: Assinatura[];
  cadin: SituacaoCadin | null;
  atualizadoEm: string;
}

export interface PaginaDeTermos {
  itens: TermoResumo[];
  total: number;
  pagina: number;
  tamanho: number;
}

export interface FiltroDeTermos {
  status?: StatusTermo;
  busca?: string;
  mesRenovacao?: number;
  pagina: number;
  tamanho: number;
}

export interface EventoDaTrilha {
  origem: 'ELABORACAO' | 'ASSINATURA' | 'CADIN' | 'ATIVACAO';
  tipo: string;
  em: string;
  detalhe: { versao?: number; papel?: PapelSignatario } & Record<string, unknown>;
}

export interface AcoesDoTermo {
  declararCadin: boolean;
  enviarParaAssinatura: boolean;
  assinarComoGestor: boolean;
  atualizarAssinaturas: boolean;
  ativar: boolean;
  publicarExtrato: boolean;
}

/** `GET /termos/:id`. */
export interface TermoDetalhe {
  id: string;
  numeroTermo: string | null;
  status: StatusTermo;
  dataInicio: string;
  dataFim: string;
  valorMensal: number | string | null;
  objeto: string | null;
  dataEmissao: string | null;
  dataAssinatura: string | null;
  dataPublicacao: string | null;
  versaoAtual: number;
  credenciado: {
    id: string;
    nome: string | null;
    tipo: 'PF' | 'PJ' | 'COOPERATIVA' | null;
    documentoMascarado: string | null;
    temAcesso: boolean;
  };
  modelo: {
    id: string;
    nome: string;
    versao: number;
    exigeGarantia: boolean;
    fundamentoDispensa: string | null;
  } | null;
  versao: {
    id: string;
    versao: number;
    hash: string;
    documento: string;
    submetidaEm: string | null;
  } | null;
  cadin: {
    impedimento: ImpedimentoCadin | null;
    consulta: 'REGULAR' | 'IRREGULAR' | 'INDISPONIVEL' | null;
    declaracoes: DeclaracaoCadin[];
  };
  assinaturas: Assinatura[];
  publicacao: { numero: string; data: string; conteudo: string } | null;
  pendenciasAtivacao: { codigo: string; descricao: string }[];
  trilha: EventoDaTrilha[];
  acoes: AcoesDoTermo;
}

export interface ModeloDoTermo {
  id: string;
  nome: string;
  versao: number;
  clausulas: string[];
  vigenteDesde: string;
  exigeGarantia: boolean;
  fundamentoDispensa: string | null;
}

export interface CredenciadoApto {
  id: string;
  nome: string | null;
  tipo: 'PF' | 'PJ' | 'COOPERATIVA' | null;
  documentoMascarado: string | null;
  temAcesso: boolean;
}

// ------------------------------------------------------------ formatação

export function numeroExibido(termo: { numeroTermo: string | null }): string {
  return termo.numeroTermo ?? 'Sem número';
}

/** Data local (AAAA-MM-DD) em formato brasileiro, sem depender do fuso do navegador. */
export function dataLegivel(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  if (!ano || !mes || !dia) return iso;
  return `${dia}/${mes}/${ano}`;
}

/** Data e hora, no fuso de São Paulo. */
export function dataHoraLegivel(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function dinheiro(valor: number | string | null | undefined): string {
  const n = typeof valor === 'string' ? Number(valor) : valor;
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Hoje em São Paulo, AAAA-MM-DD. */
export function hojeEmSaoPaulo(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
}

/** Fim da vigência de 12 meses (mesma conta do servidor), só para prévia. */
export function fimDaVigencia(inicio: string): string {
  const [a, m, d] = inicio.split('-').map(Number);
  if (!a || !m || !d) return '';
  const fim = new Date(Date.UTC(a + 1, m - 1, d));
  fim.setUTCDate(fim.getUTCDate() - 1);
  return fim.toISOString().slice(0, 10);
}

export const ROTULO_EVENTO: Record<string, string> = {
  RASCUNHO_SALVO: 'Rascunho salvo',
  VERSAO_SUBMETIDA: 'Termo elaborado',
  DECLARACAO_NADA_CONSTA: 'Declaração do CADIN: nada consta',
  DECLARACAO_INSCRITO: 'Declaração do CADIN: inscrito',
  CONVITE_EMITIDO: 'Enviado para assinatura',
  EMAIL_ENFILEIRADO: 'E-mail enviado ao credenciado',
  EMAIL_NAO_ENFILEIRADO: 'Sem e-mail: credenciado sem endereço válido',
  ENVIADO_PROVEDOR: 'Assinatura iniciada no provedor',
  ASSINADO: 'Assinado',
  RECUSADO: 'Assinatura recusada',
  ATIVADO: 'Termo ativado',
  PUBLICADO: 'Extrato publicado',
};

export const MESES_DO_ANO = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const;

// ------------------------------------------------ declaração do CADIN

/** O que o DTP informa ao registrar a declaração do Anexo III. */
export interface EntradaDaDeclaracao {
  situacao: SituacaoCadin;
  dataDeclaracao: string;
  referencia: string;
}

export function declaracaoValida(d: EntradaDaDeclaracao): string | null {
  if (!d.dataDeclaracao) return 'Informe a data da declaração do CADIN.';
  if (d.dataDeclaracao > hojeEmSaoPaulo()) return 'A data da declaração não pode ser futura.';
  if (d.referencia.trim().length < 3)
    return 'Informe a referência do documento da declaração (3 caracteres ou mais).';
  return null;
}

export const DECLARACAO_VAZIA = (): EntradaDaDeclaracao => ({
  situacao: 'NADA_CONSTA',
  dataDeclaracao: hojeEmSaoPaulo(),
  referencia: '',
});
